import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { fetchEditTargets, sendEditToProject, type EditHandoffResult, type EditTarget } from '../../services/editingApi';
import { formatStatusLabel } from '../../utils/projectStatus';
import type { SaveFirstResult } from './editStudioModel';

interface SendToEditorDialogProps {
  open: boolean;
  editId: string;
  editVersion: number;
  /** Unsaved changes would be missed: the project gets what's saved. */
  dirty: boolean;
  /** Saves now and waits, so the editor gets the version on screen. */
  prepare?: () => Promise<SaveFirstResult>;
  /** The edit's source, so a new project can start from it. */
  source?: { videoId?: string | null; multicamSetId?: string | null };
  onClose: () => void;
  onSent: (result: EditHandoffResult) => void;
}

/** The new-project form, started from the edit's video or set, coming back to the edit once it's made. */
export function newProjectLink(editId: string, source?: { videoId?: string | null; multicamSetId?: string | null }): string {
  const params = new URLSearchParams();
  if (source?.videoId) params.set('videoId', source.videoId);
  else if (source?.multicamSetId) params.set('multicamSetId', source.multicamSetId);
  params.set('returnTo', `/edit/${editId}?panel=export`);
  return `/submit-editing-request/?${params.toString()}`;
}

/** Pick one of the creator's open projects of this video; the edit becomes its tasks. */
const SendToEditorDialog: React.FC<SendToEditorDialogProps> = ({ open, editId, editVersion, dirty, prepare, source, onClose, onSent }) => {
  const [targets, setTargets] = useState<EditTarget[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTargets(null);
    setProblem(null);
    fetchEditTargets(editId)
      .then((list) => { if (!cancelled) setTargets(list); })
      .catch((error) => { if (!cancelled) { setTargets([]); setProblem(error instanceof Error ? error.message : 'Your projects couldn’t be loaded.'); } });
    return () => { cancelled = true; };
  }, [editId, open]);

  const send = async (target: EditTarget) => {
    setSending(target.id);
    setProblem(null);
    try {
      if (prepare) {
        const ready = await prepare();
        if (!ready.ok) {
          setProblem(ready.conflict ? `${ready.message} Choose Keep mine or Load theirs at the top of the studio, then send.` : ready.message);
          return;
        }
      }
      onSent(await sendEditToProject(editId, target.id));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'It couldn’t be sent.');
    } finally {
      setSending(null);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Send this edit to my editor</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The edit becomes tasks on one of your projects for this video, and the hand-off package gets a brief, the edit as data and a timeline file. Sending again replaces the earlier tasks.
        </Typography>
        {dirty ? <Alert severity="info" sx={{ mb: 1.5 }}>Sending saves your latest changes first.</Alert> : null}
        {problem ? <Alert severity="error" sx={{ mb: 1.5 }}>{problem}</Alert> : null}
        {targets === null ? <LinearProgress /> : targets.length === 0 ? (
          <Alert severity="info" action={<Button color="inherit" size="small" component={RouterLink} to={newProjectLink(editId, source)} data-testid="create-project-from-edit">Create one</Button>}>
            No open project uses this video yet. Create a project from it (it comes back here when it’s made), then send the edit there.
          </Alert>
        ) : (
          <List dense disablePadding>
            {targets.map((target) => (
              <ListItemButton key={target.id} onClick={() => send(target)} disabled={Boolean(sending)} data-testid={`send-target-${target.id}`} sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary={target.projectName}
                  secondary={`${formatStatusLabel(target.status)}${target.editorName ? ` · ${target.editorName}` : ''}${target.attachedEditVersion !== null ? ` · has version ${target.attachedEditVersion}${target.attachedEditVersion === editVersion ? ' (this one)' : ''}` : ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(sending)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendToEditorDialog;
